import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import { listClients } from '../services/adminApi';
import { formatDate } from '../utils/formatters';

export default function ClientsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    listClients()
      .then((data) => setClients(data.items || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 160,
      valueGetter: (params) =>
        params.row.name || `${params.row.first_name || ''} ${params.row.last_name || ''}`.trim() || '-',
    },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 220 },
    { field: 'phone', headerName: 'Phone', width: 150, valueGetter: (params) => params.row.phone || '-' },
    {
      field: 'business_name',
      headerName: 'Business Name',
      flex: 1,
      minWidth: 180,
      valueGetter: (params) => params.row.business_name || params.row.company || '-',
    },
    {
      field: 'assessments_count',
      headerName: 'Assessments',
      width: 110,
      valueGetter: (params) => params.row.assessments_count ?? params.row.total_assessments ?? '-',
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 170,
      valueFormatter: (params) => formatDate(params.value),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Clients
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {isMobile ? (
        <Stack spacing={1.5}>
          {clients.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">No clients yet</Typography>
            </Paper>
          )}
          {clients.map((c) => (
            <Paper key={c.id || c.client_id || c.email || Math.random()} sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                {c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>{c.email}</Typography>
              {c.phone && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{c.phone}</Typography>}
              {(c.business_name || c.company) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {c.business_name || c.company}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {(c.assessments_count ?? c.total_assessments ?? 0)} assessments · Created {formatDate(c.created_at)}
              </Typography>
            </Paper>
          ))}
        </Stack>
      ) : (
        <Box sx={{ height: 600, backgroundColor: 'background.paper', borderRadius: 1 }}>
          <DataGrid
            rows={clients}
            columns={columns}
            getRowId={(row) => row.id || row.client_id || row.email || Math.random()}
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            disableRowSelectionOnClick
          />
        </Box>
      )}
    </Box>
  );
}
